"use client";

import { c as _c } from "react/compiler-runtime";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { PageSplit } from "~/components/layout/PageSplit";
import { FaqSection } from "~/components/marketing/FaqSection";
import { Logo } from "~/components/ui/Logo";
import { isValidEmail, isValidUSPhone, toE164 } from "~/lib/auth/validation";
import { cn } from "~/lib/utils";
import { api } from "~/trpc/react";
import { WAITLIST_FAQ_ITEMS } from "./waitlistFaqData";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type FormState = "idle" | "submitting" | "success" | "error";
interface WaitlistFields {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}
type FieldErrors = Partial<Record<keyof WaitlistFields, string>>;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function validateFields(fields: WaitlistFields): FieldErrors {
  const errors: FieldErrors = {};
  if (!fields.firstName.trim()) errors.firstName = "First name is required";
  if (!fields.lastName.trim()) errors.lastName = "Last name is required";
  if (!fields.email.trim()) {
    errors.email = "Email is required";
  } else if (!isValidEmail(fields.email.trim())) {
    errors.email = "Enter a valid email address";
  }
  if (fields.phone && !isValidUSPhone(fields.phone)) {
    errors.phone = "Enter a valid 10-digit phone number";
  }
  return errors;
}

/** Format up to 10 raw digits as (555) 123-4567 */
function formatPhone(digits: string): string {
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/** Keep only digits, dropping a leading US country code, capped at 10 */
function sanitizePhone(value: string): string {
  let digits = value.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
  return digits.slice(0, 10);
}

// ---------------------------------------------------------------------------
// useConfetti — fires canvas-confetti from both bottom corners
// ---------------------------------------------------------------------------

function useConfetti(active: boolean) {
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    void (async () => {
      const confetti = (await import("canvas-confetti")).default;
      const colors = ["#2563eb", "#3b82f6", "#60a5fa", "#ffffff", "#bfdbfe"];
      const shared = {
        particleCount: 120,
        spread: 70,
        colors,
        startVelocity: 55,
        gravity: 0.9,
        ticks: 300
      };
      if (cancelled) return;

      // Left cannon
      void confetti({
        ...shared,
        origin: {
          x: 0.1,
          y: 1
        },
        angle: 60
      });
      // Right cannon
      void confetti({
        ...shared,
        origin: {
          x: 0.9,
          y: 1
        },
        angle: 120
      });

      // Second burst after 600ms
      setTimeout(() => {
        if (cancelled) return;
        void confetti({
          ...shared,
          particleCount: 80,
          origin: {
            x: 0.2,
            y: 1
          },
          angle: 70
        });
        void confetti({
          ...shared,
          particleCount: 80,
          origin: {
            x: 0.8,
            y: 1
          },
          angle: 110
        });
      }, 600);

      // Gentle shower at 1.4s
      setTimeout(() => {
        if (cancelled) return;
        void confetti({
          particleCount: 60,
          spread: 120,
          colors,
          origin: {
            x: 0.5,
            y: 0
          },
          startVelocity: 20,
          gravity: 0.6,
          ticks: 400
        });
      }, 1400);
    })();
    return () => {
      cancelled = true;
    };
  }, [active]);
}

// ---------------------------------------------------------------------------
// WaitlistPage
// ---------------------------------------------------------------------------

function getErrorMessage(err: unknown): string {
  if (err && typeof err === "object" && "data" in err) {
    const trpcErr = err as {
      data?: {
        code?: string;
      };
      message?: string;
    };
    if (trpcErr.data?.code === "BAD_REQUEST") {
      return "Please check your details and try again.";
    }
  }
  return "Something went wrong. Please try again.";
}
export function WaitlistPage() {
  const $ = _c(31);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      firstName: "",
      lastName: "",
      email: "",
      phone: ""
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [fields, setFields] = useState(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [fieldErrors, setFieldErrors] = useState(t1);
  const [formState, setFormState] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [touched, setTouched] = useState(t2);
  const [successName, setSuccessName] = useState("");
  let t3;
  if ($[3] !== fields) {
    t3 = () => {
      setSuccessName(fields.firstName);
      setFormState("success");
    };
    $[3] = fields;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = err => {
      setErrorMessage(getErrorMessage(err));
      setFormState("error");
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3) {
    t5 = {
      onSuccess: t3,
      onError: t4
    };
    $[6] = t3;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const joinMutation = api.waitlist.join.useMutation(t5);
  let t6;
  if ($[8] !== touched) {
    t6 = function handleChange(key, value) {
      setFields(prev => ({
        ...prev,
        [key]: value
      }));
      if (touched[key]) {
        setFieldErrors(prev_0 => ({
          ...prev_0,
          [key]: undefined
        }));
      }
    };
    $[8] = touched;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const handleChange = t6;
  let t7;
  if ($[10] !== fields) {
    t7 = function handleBlur(key_0) {
      setTouched(prev_1 => ({
        ...prev_1,
        [key_0]: true
      }));
      const errors = validateFields({
        ...fields
      });
      setFieldErrors(prev_2 => ({
        ...prev_2,
        [key_0]: errors[key_0]
      }));
    };
    $[10] = fields;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const handleBlur = t7;
  let t8;
  if ($[12] !== fields || $[13] !== joinMutation) {
    t8 = function handleSubmit(e) {
      e.preventDefault();
      setTouched({
        firstName: true,
        lastName: true,
        email: true,
        phone: true
      });
      const errors_0 = validateFields(fields);
      setFieldErrors(errors_0);
      if (Object.keys(errors_0).length > 0) {
        return;
      }
      setFormState("submitting");
      joinMutation.mutate({
        first_name: fields.firstName,
        last_name: fields.lastName,
        email: fields.email,
        phone: fields.phone ? toE164(fields.phone) : undefined
      });
    };
    $[12] = fields;
    $[13] = joinMutation;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const handleSubmit = t8;
  if (formState === "success") {
    let t9;
    if ($[15] !== fields.email || $[16] !== successName) {
      t9 = <SuccessPage name={successName} email={fields.email} />;
      $[15] = fields.email;
      $[16] = successName;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    return t9;
  }
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <br />;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <>Save your spot.{t9}We're almost <em className="italic">ready.</em></>;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== errorMessage || $[21] !== fieldErrors || $[22] !== fields || $[23] !== formState || $[24] !== handleBlur || $[25] !== handleChange || $[26] !== handleSubmit) {
    t11 = <PageSplit leftTitle={t10} leftDescription="EZSpot is launching soon. Join the waitlist and we'll email you the moment parking spots are available in your area."><WaitlistForm fields={fields} fieldErrors={fieldErrors} formState={formState} errorMessage={errorMessage} onChange={handleChange} onBlur={handleBlur} onSubmit={handleSubmit} /></PageSplit>;
    $[20] = errorMessage;
    $[21] = fieldErrors;
    $[22] = fields;
    $[23] = formState;
    $[24] = handleBlur;
    $[25] = handleChange;
    $[26] = handleSubmit;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <FaqSection items={WAITLIST_FAQ_ITEMS} />;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t11) {
    t13 = <>{t11}{t12}</>;
    $[29] = t11;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  return t13;
}

// ---------------------------------------------------------------------------
// SuccessPage — full viewport celebration
// ---------------------------------------------------------------------------

function SuccessPage(t0) {
  const $ = _c(14);
  const {
    name,
    email
  } = t0;
  useConfetti(true);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      background: "var(--color-surface)"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="pointer-events-none absolute inset-0" style={{
      background: "radial-gradient(ellipse 70% 50% at 50% 60%, rgba(37,99,235,0.07) 0%, transparent 70%)"
    }} />;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Logo href="/" variant="default" sizeClassName="text-4xl" />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex h-20 w-20 items-center justify-center rounded-full text-[40px]" style={{
      background: "var(--color-surface-2)",
      border: "1px solid var(--color-border)"
    }}>🎉</div>;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== name) {
    t5 = <h1 className="text-[32px] font-bold leading-tight text-body">You're in, <span className="text-accent">{name}!</span></h1>;
    $[4] = name;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== email) {
    t6 = <p className="text-[15px] leading-relaxed text-secondary">We'll notify you at{" "}<span className="font-semibold text-body">{email}</span> the moment EZSpot is live.</p>;
    $[6] = email;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t5 || $[9] !== t6) {
    t7 = <div className="flex flex-col gap-2">{t5}{t6}</div>;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <a href="/" className="rounded-xl border border-border px-6 py-2.5 text-[14px] font-semibold text-body transition-colors hover:border-accent hover:text-accent focus:outline-none">Back to home</a>;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t7) {
    t9 = <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-6 py-16" style={t1}>{t2}<div className="relative z-10 flex max-w-[480px] flex-col items-center gap-8 text-center">{t3}{t4}{t7}{t8}</div></div>;
    $[12] = t7;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  return t9;
}

// ---------------------------------------------------------------------------
// WaitlistForm
// ---------------------------------------------------------------------------

interface WaitlistFormProps {
  fields: WaitlistFields;
  fieldErrors: FieldErrors;
  formState: FormState;
  errorMessage: string;
  onChange: (key: keyof WaitlistFields, value: string) => void;
  onBlur: (key: keyof WaitlistFields) => void;
  onSubmit: (e: React.FormEvent) => void;
}
function WaitlistForm(t0) {
  const $ = _c(97);
  const {
    fields,
    fieldErrors,
    formState,
    errorMessage,
    onChange,
    onBlur,
    onSubmit
  } = t0;
  const isSubmitting = formState === "submitting";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div><h2 className="text-[22px] font-bold text-body">Join the waitlist</h2><p className="mt-1 text-[14px] text-secondary">We'll let you know as soon as EZSpot is available in your city.</p></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== errorMessage || $[2] !== formState) {
    t2 = formState === "error" && <div className="rounded-xl border border-(--color-danger)/30 bg-(--color-danger-bg) px-4 py-3 text-[13px] text-(--color-danger)">{errorMessage}</div>;
    $[1] = errorMessage;
    $[2] = formState;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = fieldErrors.firstName;
  const t4 = !!fieldErrors.firstName;
  const t5 = fieldErrors.firstName ? "waitlist-first-name-error" : undefined;
  const t6 = fields.firstName;
  let t7;
  if ($[4] !== onChange) {
    t7 = e => onChange("firstName", e.target.value);
    $[4] = onChange;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== onBlur) {
    t8 = () => onBlur("firstName");
    $[6] = onBlur;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const t9 = !!fieldErrors.firstName;
  let t10;
  if ($[8] !== t9) {
    t10 = inputClass(t9);
    $[8] = t9;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== fields.firstName || $[11] !== isSubmitting || $[12] !== t10 || $[13] !== t4 || $[14] !== t5 || $[15] !== t7 || $[16] !== t8) {
    t11 = <input id="waitlist-first-name" type="text" required={true} aria-invalid={t4} aria-describedby={t5} value={t6} onChange={t7} onBlur={t8} placeholder="Joe" disabled={isSubmitting} className={t10} />;
    $[10] = fields.firstName;
    $[11] = isSubmitting;
    $[12] = t10;
    $[13] = t4;
    $[14] = t5;
    $[15] = t7;
    $[16] = t8;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== fieldErrors.firstName || $[19] !== t11) {
    t12 = <FormField label="First name" inputId="waitlist-first-name" error={t3} required={true}>{t11}</FormField>;
    $[18] = fieldErrors.firstName;
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const t13 = fieldErrors.lastName;
  const t14 = !!fieldErrors.lastName;
  const t15 = fieldErrors.lastName ? "waitlist-last-name-error" : undefined;
  const t16 = fields.lastName;
  let t17;
  if ($[21] !== onChange) {
    t17 = e_0 => onChange("lastName", e_0.target.value);
    $[21] = onChange;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  let t18;
  if ($[23] !== onBlur) {
    t18 = () => onBlur("lastName");
    $[23] = onBlur;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  const t19 = !!fieldErrors.lastName;
  let t20;
  if ($[25] !== t19) {
    t20 = inputClass(t19);
    $[25] = t19;
    $[26] = t20;
  } else {
    t20 = $[26];
  }
  let t21;
  if ($[27] !== fields.lastName || $[28] !== isSubmitting || $[29] !== t14 || $[30] !== t15 || $[31] !== t17 || $[32] !== t18 || $[33] !== t20) {
    t21 = <input id="waitlist-last-name" required={true} aria-invalid={t14} aria-describedby={t15} value={t16} onChange={t17} onBlur={t18} placeholder="Easy" disabled={isSubmitting} className={t20} />;
    $[27] = fields.lastName;
    $[28] = isSubmitting;
    $[29] = t14;
    $[30] = t15;
    $[31] = t17;
    $[32] = t18;
    $[33] = t20;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  let t22;
  if ($[35] !== fieldErrors.lastName || $[36] !== t21) {
    t22 = <FormField label="Last name" inputId="waitlist-last-name" error={t13} required={true}>{t21}</FormField>;
    $[35] = fieldErrors.lastName;
    $[36] = t21;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  let t23;
  if ($[38] !== t12 || $[39] !== t22) {
    t23 = <div className="grid grid-cols-2 gap-3">{t12}{t22}</div>;
    $[38] = t12;
    $[39] = t22;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  const t24 = fieldErrors.email;
  const t25 = !!fieldErrors.email;
  const t26 = fieldErrors.email ? "waitlist-email-error" : undefined;
  const t27 = fields.email;
  let t28;
  if ($[41] !== onChange) {
    t28 = e_1 => onChange("email", e_1.target.value);
    $[41] = onChange;
    $[42] = t28;
  } else {
    t28 = $[42];
  }
  let t29;
  if ($[43] !== onBlur) {
    t29 = () => onBlur("email");
    $[43] = onBlur;
    $[44] = t29;
  } else {
    t29 = $[44];
  }
  const t30 = !!fieldErrors.email;
  let t31;
  if ($[45] !== t30) {
    t31 = inputClass(t30);
    $[45] = t30;
    $[46] = t31;
  } else {
    t31 = $[46];
  }
  let t32;
  if ($[47] !== fields.email || $[48] !== isSubmitting || $[49] !== t25 || $[50] !== t26 || $[51] !== t28 || $[52] !== t29 || $[53] !== t31) {
    t32 = <input id="waitlist-email" type="email" required={true} aria-invalid={t25} aria-describedby={t26} value={t27} onChange={t28} onBlur={t29} placeholder="joelovesgoodparking@example.com" disabled={isSubmitting} className={t31} />;
    $[47] = fields.email;
    $[48] = isSubmitting;
    $[49] = t25;
    $[50] = t26;
    $[51] = t28;
    $[52] = t29;
    $[53] = t31;
    $[54] = t32;
  } else {
    t32 = $[54];
  }
  let t33;
  if ($[55] !== fieldErrors.email || $[56] !== t32) {
    t33 = <FormField label="Email" inputId="waitlist-email" error={t24} required={true}>{t32}</FormField>;
    $[55] = fieldErrors.email;
    $[56] = t32;
    $[57] = t33;
  } else {
    t33 = $[57];
  }
  const t34 = fieldErrors.phone;
  let t35;
  let t36;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-[14px] text-muted">+1</span>;
    t36 = <span id="waitlist-phone-format" className="sr-only">US phone number, +1 country code</span>;
    $[58] = t35;
    $[59] = t36;
  } else {
    t35 = $[58];
    t36 = $[59];
  }
  const t37 = !!fieldErrors.phone;
  const t38 = fieldErrors.phone && "waitlist-phone-error";
  let t39;
  if ($[60] !== t38) {
    t39 = cn("waitlist-phone-hint", "waitlist-phone-format", t38);
    $[60] = t38;
    $[61] = t39;
  } else {
    t39 = $[61];
  }
  let t40;
  if ($[62] !== fields.phone) {
    t40 = formatPhone(fields.phone);
    $[62] = fields.phone;
    $[63] = t40;
  } else {
    t40 = $[63];
  }
  let t41;
  if ($[64] !== onChange) {
    t41 = e_2 => onChange("phone", sanitizePhone(e_2.target.value));
    $[64] = onChange;
    $[65] = t41;
  } else {
    t41 = $[65];
  }
  let t42;
  if ($[66] !== onBlur) {
    t42 = () => onBlur("phone");
    $[66] = onBlur;
    $[67] = t42;
  } else {
    t42 = $[67];
  }
  let t43;
  if ($[68] !== fieldErrors.phone) {
    t43 = cn(inputClass(!!fieldErrors.phone), "pl-10");
    $[68] = fieldErrors.phone;
    $[69] = t43;
  } else {
    t43 = $[69];
  }
  let t44;
  if ($[70] !== isSubmitting || $[71] !== t37 || $[72] !== t39 || $[73] !== t40 || $[74] !== t41 || $[75] !== t42 || $[76] !== t43) {
    t44 = <div className="relative">{t35}{t36}<input id="waitlist-phone" type="tel" inputMode="numeric" autoComplete="tel-national" aria-invalid={t37} aria-describedby={t39} value={t40} onChange={t41} onBlur={t42} placeholder="(415) 867-5309" disabled={isSubmitting} className={t43} /></div>;
    $[70] = isSubmitting;
    $[71] = t37;
    $[72] = t39;
    $[73] = t40;
    $[74] = t41;
    $[75] = t42;
    $[76] = t43;
    $[77] = t44;
  } else {
    t44 = $[77];
  }
  let t45;
  if ($[78] !== fieldErrors.phone || $[79] !== t44) {
    t45 = <FormField label="Phone number" inputId="waitlist-phone" error={t34} hint="Optional">{t44}</FormField>;
    $[78] = fieldErrors.phone;
    $[79] = t44;
    $[80] = t45;
  } else {
    t45 = $[80];
  }
  let t46;
  if ($[81] !== isSubmitting) {
    t46 = isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />;
    $[81] = isSubmitting;
    $[82] = t46;
  } else {
    t46 = $[82];
  }
  const t47 = isSubmitting ? "Joining\u2026" : "Join the waitlist";
  let t48;
  if ($[83] !== isSubmitting || $[84] !== t46 || $[85] !== t47) {
    t48 = <button type="submit" disabled={isSubmitting} className="mt-1 flex w-full items-center justify-center gap-2 rounded-xl bg-accent py-3 text-[14px] font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2">{t46}{t47}</button>;
    $[83] = isSubmitting;
    $[84] = t46;
    $[85] = t47;
    $[86] = t48;
  } else {
    t48 = $[86];
  }
  let t49;
  if ($[87] !== onSubmit || $[88] !== t23 || $[89] !== t33 || $[90] !== t45 || $[91] !== t48) {
    t49 = <form onSubmit={onSubmit} noValidate={true} className="flex flex-col gap-4">{t23}{t33}{t45}{t48}</form>;
    $[87] = onSubmit;
    $[88] = t23;
    $[89] = t33;
    $[90] = t45;
    $[91] = t48;
    $[92] = t49;
  } else {
    t49 = $[92];
  }
  let t50;
  if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = <p className="text-center text-[12px] text-muted">No spam, ever.</p>;
    $[93] = t50;
  } else {
    t50 = $[93];
  }
  let t51;
  if ($[94] !== t2 || $[95] !== t49) {
    t51 = <div className="flex flex-col gap-6">{t1}{t2}{t49}{t50}</div>;
    $[94] = t2;
    $[95] = t49;
    $[96] = t51;
  } else {
    t51 = $[96];
  }
  return t51;
}

// ---------------------------------------------------------------------------
// FormField
// ---------------------------------------------------------------------------

function FormField(t0) {
  const $ = _c(19);
  const {
    label,
    inputId,
    error,
    hint,
    required,
    children
  } = t0;
  let t1;
  if ($[0] !== required) {
    t1 = required && <span aria-hidden="true" className="ml-1 text-(--color-danger)">*</span>;
    $[0] = required;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== inputId || $[3] !== label || $[4] !== t1) {
    t2 = <label htmlFor={inputId} className="text-[13px] font-semibold text-body">{label}{t1}</label>;
    $[2] = inputId;
    $[3] = label;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== hint || $[7] !== inputId) {
    t3 = hint && <span id={`${inputId}-hint`} className="text-[11px] text-muted">{hint}</span>;
    $[6] = hint;
    $[7] = inputId;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t2 || $[10] !== t3) {
    t4 = <div className="flex items-baseline justify-between">{t2}{t3}</div>;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== error || $[13] !== inputId) {
    t5 = error && <p id={`${inputId}-error`} role="alert" className="text-[11px] text-(--color-danger)">{error}</p>;
    $[12] = error;
    $[13] = inputId;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== children || $[16] !== t4 || $[17] !== t5) {
    t6 = <div className="flex flex-col gap-1.5">{t4}{children}{t5}</div>;
    $[15] = children;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function inputClass(hasError: boolean) {
  return ["w-full rounded-xl border px-4 py-2.5 text-[14px] text-body outline-none transition-colors placeholder:text-muted", "disabled:cursor-not-allowed disabled:opacity-60", hasError ? "border-(--color-danger) bg-surface ring-2 ring-(--color-danger)/20 focus:border-(--color-danger)" : "border-border bg-surface-2 focus:border-accent focus:bg-surface focus:ring-2 focus:ring-accent/10"].join(" ");
}